// src/pages/Finance/OverduePayments/Overdue/ClientInvoicesDrawer.jsx
import React, { useMemo } from "react"
import { Drawer, Table, Typography, Tag, Empty } from "antd"
import moment from "moment"

const { Title, Text } = Typography

// Helper: Format money (keep as is)
const money = (n = 0) =>
  Number(n).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

// Helper: Format date (keep as is)
const formatDate = (ts) => {
  if (!ts) return "—"
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000)
    return moment(d).format("DD.MM.YYYY")
  } catch (error) {
    console.error("Error formatting date in drawer:", error, ts)
    if (typeof ts === "string" && /^\d{2}\.\d{2}\.\d{4}$/.test(ts)) {
      return ts
    }
    return "Invalid Date"
  }
}

// --- Component ---
export default function ClientInvoicesDrawer({ visible, onClose, clientData, availableDocs = [] }) {
  // Use useMemo to filter the documents passed from the parent
  const clientDocuments = useMemo(() => {
    if (!clientData?.id || !availableDocs) {
      return []
    }

    // --- This filter logic is confirmed correct by your logs ---
    const filteredAndSortedDocs = availableDocs
      .filter((doc) => doc.clientId === clientData.id) // Checks if doc's userId matches the selected client's id
      .sort((a, b) => {
        const da = a.documentDate?.toDate
          ? a.documentDate.toDate()
          : new Date(a.documentDate?.seconds * 1000 || 0)
        const db = b.documentDate?.toDate
          ? b.documentDate.toDate()
          : new Date(b.documentDate?.seconds * 1000 || 0)
        return db.valueOf() - da.valueOf() // Sort descending by date
      })
    // --- End Filter Logic ---

    return filteredAndSortedDocs
  }, [clientData?.id, availableDocs]) // Dependencies

  // --- Columns Definition (remains the same) ---
  const columns = [
    {
      title: "Doc. No.",
      dataIndex: "docNumber",
      key: "docNumber",
      render: (num, rec) => {
        const s = String(num || "").padStart(4, "0")
        const prefix = rec.docType === "proforma" ? "PRO" : rec.docPrefix || "INV"
        return `${prefix}${s}`
      },
    },
    {
      title: "Type",
      dataIndex: "docType",
      key: "docType",
      align: "center",
      render: (docType) => (
        <Tag color={docType === "invoice" ? "blue" : "cyan"}>
          {docType === "invoice" ? "Invoice" : "Proforma"}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "documentDate",
      key: "date",
      render: formatDate,
    },
    {
      title: "Total (Eur)",
      dataIndex: ["totalData", "totalSum"],
      key: "totalSum",
      align: "right",
      render: (v) => money(v),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "status",
      align: "center",
      render: () => <Tag color="red">Unpaid</Tag>,
    },
    {
      title: "Pay Until",
      dataIndex: "payUntilFormatted",
      key: "payUntil",
      align: "center",
      render: (v) => v || "—",
    },
  ]

  // --- Render Logic (remains the same) ---
  const drawerTitle = (
    <Title level={4}>
      Overdue Documents for: <Text type="secondary">{clientData?.name || "Selected Client"}</Text>
    </Title>
  )

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      onClose={onClose}
      open={visible}
      width={720}
      destroyOnClose>
      {clientDocuments.length === 0 ? (
        <Empty
          description={
            clientData?.id
              ? `No overdue documents found for ${
                  clientData.name || "this client"
                } in the current list.`
              : "No client selected."
          }
        />
      ) : (
        <Table
          columns={columns}
          dataSource={clientDocuments}
          rowKey="id"
          pagination={{ pageSize: 15, size: "small", position: ["bottomRight"] }}
          size="small"
          scroll={{ y: "calc(100vh - 210px)", x: "max-content" }}
        />
      )}
    </Drawer>
  )
}
